import { AnimatePresence, motion } from 'framer-motion';
import { FC, useId, useMemo } from 'react';

import { Box } from '@bonusly/design-system';

import { BoredSVG, STATE_PARTS } from './states';
import { BizyProps, BizySize, BizyState, isPartAnimatedState, PartAnimatedState } from './types';

/**
 * Bizy - The Bonusly bee mascot component
 *
 * A cute bee character that can display different emotional states.
 * Supports smooth part-based animations between states (antennae, wings, body, eyes, arms).
 * The bored state uses crossfade transition due to its unique rotated pose.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Bizy state="excited" />
 *
 * // With custom size
 * <Bizy state="thinking" size="lg" />
 *
 * // Numeric size
 * <Bizy state="determined" size={100} />
 * ```
 */
export const Bizy: FC<BizyProps> = ({ state = 'curious', size = 'md', className }) => {
	const sizeValue = useMemo(() => getSizeValue(size), [size]);

	return (
		<Box className={className} display="inline-flex" alignItems="center" justifyContent="center">
			<AnimatedBizySVG state={state} width={sizeValue} height={sizeValue} />
		</Box>
	);
};

// Animation configuration

const TRANSITION = {
	duration: 0.3,
	ease: [0.4, 0, 0.2, 1] as const,
};

const SPRING_TRANSITION = {
	type: 'spring' as const,
	stiffness: 200,
	damping: 20,
};

const WING_FLUTTER = {
	rotate: [0, -0.5, 0, 0.5, 0],
	scale: [1, 1.01, 1, 0.99, 1],
	transition: {
		duration: 0.05,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
	},
};

const WING_FLUTTER_OFFSET = {
	rotate: [0, 0.5, 0, -0.5, 0],
	scale: [1, 0.99, 1, 1.01, 1],
	transition: {
		duration: 0.05,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
		delay: 0.01,
	},
};

const getSizeValue = (size: BizySize): number => {
	if (typeof size === 'number') return size;
	const sizeMap: Record<string, number> = { sm: 48, md: 80, lg: 120 };
	return sizeMap[size] || 80;
};

// Animated SVG component
interface AnimatedBizyProps {
	state: BizyState;
	width: number;
	height: number;
	className?: string;
}

const AnimatedBizySVG: FC<AnimatedBizyProps> = ({ state, width, height, className }) => {
	const instanceId = useId();
	const animatedState: PartAnimatedState = isPartAnimatedState(state) ? state : 'curious';
	const parts = useMemo(() => STATE_PARTS[animatedState], [animatedState]);
	const clipId = `clip_bizy_${instanceId}`;

	const leftArm = parts.arms.find((a) => a.side === 'left');
	const rightArm = parts.arms.find((a) => a.side === 'right');
	const isBored = state === 'bored';

	return (
		<AnimatePresence mode="wait">
			{!isBored ? (
				<motion.svg
					key="animated"
					className={className}
					width={width}
					height={height}
					viewBox="0 0 316 278"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.25 }}
				>
					{/* Left arm (behind body) */}
					{leftArm && (
						<motion.path
							d={leftArm.d}
							stroke="#051236"
							strokeWidth="18.2362"
							strokeLinecap="round"
							fill="none"
							initial={false}
							animate={{ d: leftArm.d }}
							transition={TRANSITION}
						/>
					)}

					{/* Wings (behind body) - with flutter animation */}
					<motion.g style={{ mixBlendMode: 'multiply', originX: '175px', originY: '120px' }} animate={WING_FLUTTER}>
						<motion.path d={parts.wing1Path} fill="#DBDFE6" initial={false} animate={{ d: parts.wing1Path }} transition={TRANSITION} />
					</motion.g>
					<motion.g style={{ mixBlendMode: 'multiply', originX: '175px', originY: '140px' }} animate={WING_FLUTTER_OFFSET}>
						<motion.path d={parts.wing2Path} fill="#DBDFE6" initial={false} animate={{ d: parts.wing2Path }} transition={TRANSITION} />
					</motion.g>
					{/* Additional wings (for states with 4 wings) */}
					{parts.wing3Path && (
						<motion.g style={{ mixBlendMode: 'multiply', originX: '130px', originY: '120px' }} animate={WING_FLUTTER}>
							<motion.path d={parts.wing3Path} fill="#DBDFE6" initial={false} animate={{ d: parts.wing3Path }} transition={TRANSITION} />
						</motion.g>
					)}
					{parts.wing4Path && (
						<motion.g style={{ mixBlendMode: 'multiply', originX: '130px', originY: '140px' }} animate={WING_FLUTTER_OFFSET}>
							<motion.path d={parts.wing4Path} fill="#DBDFE6" initial={false} animate={{ d: parts.wing4Path }} transition={TRANSITION} />
						</motion.g>
					)}

					{/* Antennae */}
					{parts.antenna1.filled ? (
						<motion.path fill="#051236" initial={false} animate={{ d: parts.antenna1.path }} transition={SPRING_TRANSITION} />
					) : (
						<>
							<motion.path
								d={parts.antenna1.path}
								stroke="#051236"
								strokeWidth="5"
								strokeLinecap="round"
								fill="none"
								initial={false}
								animate={{ d: parts.antenna1.path }}
								transition={SPRING_TRANSITION}
							/>
							{parts.antenna1.tip && (
								<motion.circle
									fill="#051236"
									stroke="#051236"
									initial={false}
									animate={{ cx: parts.antenna1.tip.cx, cy: parts.antenna1.tip.cy, r: parts.antenna1.tip.r }}
									transition={SPRING_TRANSITION}
								/>
							)}
						</>
					)}
					{parts.antenna2.filled ? (
						<motion.path fill="#051236" initial={false} animate={{ d: parts.antenna2.path }} transition={SPRING_TRANSITION} />
					) : (
						<>
							<motion.path
								d={parts.antenna2.path}
								stroke="#051236"
								strokeWidth="5"
								strokeLinecap="round"
								fill="none"
								initial={false}
								animate={{ d: parts.antenna2.path }}
								transition={SPRING_TRANSITION}
							/>
							{parts.antenna2.tip && (
								<motion.circle
									fill="#051236"
									stroke="#051236"
									initial={false}
									animate={{ cx: parts.antenna2.tip.cx, cy: parts.antenna2.tip.cy, r: parts.antenna2.tip.r }}
									transition={SPRING_TRANSITION}
								/>
							)}
						</>
					)}

					{/* Body with clip path */}
					<defs>
						<clipPath id={clipId}>
							<motion.rect
								fill="white"
								initial={false}
								animate={{ x: parts.body.x, y: parts.body.y, width: parts.body.width, height: parts.body.height, rx: parts.body.rx }}
								transition={SPRING_TRANSITION}
							/>
						</clipPath>
					</defs>

					<g clipPath={`url(#${clipId})`}>
						{/* Main body */}
						<motion.rect
							fill="#FCC84F"
							initial={false}
							animate={{ x: parts.body.x, y: parts.body.y, width: parts.body.width, height: parts.body.height, rx: parts.body.rx }}
							transition={SPRING_TRANSITION}
						/>

						{/* Body highlight */}
						{typeof parts.bodyHighlight === 'string' ? (
							<motion.path fill="#FDDF8E" initial={false} animate={{ d: parts.bodyHighlight }} transition={SPRING_TRANSITION} />
						) : (
							<motion.rect
								fill="#FDDF8E"
								initial={false}
								animate={{
									x: parts.bodyHighlight.x,
									y: parts.bodyHighlight.y,
									width: parts.bodyHighlight.width,
									height: parts.bodyHighlight.height,
									rx: parts.bodyHighlight.rx,
								}}
								transition={SPRING_TRANSITION}
							/>
						)}

						{/* Stripes */}
						<g style={{ mixBlendMode: 'darken' }}>
							<motion.rect
								fill="#051236"
								initial={false}
								animate={{ x: parts.body.x, y: parts.stripes.y1, width: parts.body.width, height: parts.stripes.height }}
								transition={SPRING_TRANSITION}
							/>
						</g>
						<g style={{ mixBlendMode: 'darken' }}>
							<motion.rect
								fill="#051236"
								initial={false}
								animate={{ x: parts.body.x, y: parts.stripes.y2, width: parts.body.width, height: parts.stripes.height }}
								transition={SPRING_TRANSITION}
							/>
						</g>
						<g style={{ mixBlendMode: 'darken' }}>
							<motion.rect
								fill="#051236"
								initial={false}
								animate={{ x: parts.body.x, y: parts.stripes.y3, width: parts.body.width, height: parts.stripes.height }}
								transition={SPRING_TRANSITION}
							/>
						</g>

						{/* Left arm inside clip (for thinking state) */}
						{animatedState === 'thinking' && leftArm && (
							<motion.path
								d={leftArm.d}
								stroke="#051236"
								strokeWidth="18.2362"
								strokeLinecap="round"
								fill="none"
								initial={false}
								animate={{ d: leftArm.d }}
								transition={TRANSITION}
							/>
						)}
					</g>

					{/* Eyes */}
					{'leftPath' in parts.eyes ? (
						<>
							<motion.path fill="#051236" initial={false} animate={{ d: parts.eyes.leftPath }} transition={SPRING_TRANSITION} />
							<motion.path fill="#051236" initial={false} animate={{ d: parts.eyes.rightPath }} transition={SPRING_TRANSITION} />
						</>
					) : (
						<>
							<motion.rect
								fill="#051236"
								initial={false}
								animate={{ x: parts.eyes.leftX, y: parts.eyes.y, width: parts.eyes.width, height: parts.eyes.height, rx: parts.eyes.rx }}
								transition={SPRING_TRANSITION}
							/>
							<motion.rect
								fill="#051236"
								initial={false}
								animate={{ x: parts.eyes.rightX, y: parts.eyes.y, width: parts.eyes.width, height: parts.eyes.height, rx: parts.eyes.rx }}
								transition={SPRING_TRANSITION}
							/>
						</>
					)}

					{/* Communicator Badge (Star Trek style) */}
					{parts.badge && (
						<g>
							{/* Outer ellipse - dark brown */}
							<motion.ellipse
								fill="#4D3505"
								initial={false}
								animate={{
									cx: parts.badge.outerEllipse.cx,
									cy: parts.badge.outerEllipse.cy,
									rx: parts.badge.outerEllipse.rx,
									ry: parts.badge.outerEllipse.ry,
								}}
								transition={SPRING_TRANSITION}
							/>
							{/* Inner ellipse - gold */}
							<motion.ellipse
								fill="#B37E00"
								initial={false}
								animate={{
									cx: parts.badge.innerEllipse.cx,
									cy: parts.badge.innerEllipse.cy,
									rx: parts.badge.innerEllipse.rx,
									ry: parts.badge.innerEllipse.ry,
								}}
								transition={SPRING_TRANSITION}
							/>
							{/* Star shape */}
							<motion.path
								fill="#DBDFE6"
								fillRule="evenodd"
								clipRule="evenodd"
								initial={false}
								animate={{ d: parts.badge.starPath }}
								transition={SPRING_TRANSITION}
							/>
							{/* Star highlight */}
							<motion.path fill="#F6F6F9" initial={false} animate={{ d: parts.badge.starHighlightPath }} transition={SPRING_TRANSITION} />
						</g>
					)}

					{/* Stinger */}
					<motion.path fill="#051236" initial={false} animate={{ d: parts.stinger }} transition={TRANSITION} />

					{/* Right arm (in front of body) */}
					{rightArm && (
						<motion.path
							d={rightArm.d}
							stroke="#051236"
							strokeWidth="18.2362"
							strokeLinecap="round"
							fill="none"
							initial={false}
							animate={{ d: rightArm.d }}
							transition={TRANSITION}
						/>
					)}

					{/* Stars (for excited state) - with twinkle animation */}
					{parts.stars?.map((starPath, index) => (
						<motion.path
							key={`star-${index}`}
							d={starPath}
							fill="#E6AE14"
							initial={{ opacity: 0, scale: 0 }}
							animate={{
								opacity: [0, 1, 0.7, 1, 0.8, 1],
								scale: [0, 1.2, 0.9, 1.1, 0.95, 1],
								rotate: [0, 0, -5, 5, -3, 0],
							}}
							transition={{
								duration: 1.5,
								delay: index * 0.15,
								repeat: Infinity,
								repeatDelay: 0.5,
								ease: 'easeInOut',
							}}
							style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
						/>
					))}
				</motion.svg>
			) : (
				<motion.div key="bored" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className={className}>
					<BoredSVG width={width} height={height} />
				</motion.div>
			)}
		</AnimatePresence>
	);
};
